import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import { arrayMove } from '@dnd-kit/sortable';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-apollo';
import qs from 'query-string';

import DesignEditor from '../../../../../../components/global/DesignEditor';
import DesignEditorHeader, {
  DesignEditorHeaderLeftSlot,
} from '../../../../../../components/global/DesignEditor/components/DesignEditorHeader';
import DesignEditorControls, {
  DesignEditorSecondaryControls,
} from '../../../../../../components/global/DesignEditor/components/DesignEditorControls';
import DesignEditorPreviewPane, {
  DesignEditorPreviewContent,
} from '../../../../../../components/global/DesignEditor/components/DesignEditorPreviewPane';
import Button from '../../../../../../components/global/Button';
import Angle from '../../../../../../components/global/Angle';
import {
  isNativeAppWebView,
  postNativeAppAlbumSlideshowDesignerDismiss,
} from '../../../../../../services/native-bridge/native-apps-interface';
import useVideoSlideshow from '../hooks/useVideoSlideshow';
import useVideoSlideshowPlayback from '../../../../../../components/grid/components/VideoSlideshowGridViewer/hooks/useVideoSlideshowPlayback';
import Spinner from '../../../../../../components/global/Spinner';
import { ALBUM_SLIDESHOW_VIDEO_LAYOUT } from '../../../../../../constants/albums';
import useOpenAlert from '../../../../../../hooks/useOpenAlert';
import { getErrorString } from '../../../../../../util/normalize-errors';
import AlbumSlideshowShareModal, {
  ALBUM_SLIDESHOW_SHARE_MODAL,
} from '../../../../../../components/global/AlbumSlideshowShareModal';
import useOpenModal from '../../../../../../hooks/useOpenModal';
import useCloseModal from '../../../../../../hooks/useCloseModal';
import ContributorShare from '../../../../components/ContributorShare';
import ContactCircleSaveModal from '../../../../../../components/global/ContactCircleSaveModal/modal';
import ContactCircleShareModal from '../../../../../../components/global/ContactCircleShareModal';
import { CONTRIBUTOR_SHARE_MODAL } from '../../../../../../constants/modals';
import { MeQuery } from '../../../../../../graphql/queries/account';
import SecondaryVideosView from './SecondaryVideosView';
import { getAsset } from '../../../../../../util/assets';
import { isAlbumOwner } from '../../../../../../util/account';
import PlayButtonIcon from '../../../../../../components/icons/PlayButtonIcon';
import { isVGBContext } from '../../../../../../util/album';

const CONTROL_PANE = {
  VIDEOS: 'VIDEOS',
};

const propTypes = {
  basename: PropTypes.string,
  history: PropTypes.shape({
    replace: PropTypes.func,
    push: PropTypes.func,
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      albumId: PropTypes.string,
    }),
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
};

const VideoSlideshowDesignerView = ({ basename, history, match, location }) => {
  const { albumId } = match.params;
  const openAlert = useOpenAlert();
  const openModal = useOpenModal();
  const closeModal = useCloseModal();
  const isNativeView = isNativeAppWebView();
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const [activeControlPane] = useState(CONTROL_PANE.VIDEOS);
  const {
    album,
    saving,
    slideshow,
    onSlideshowUpdate,
    onLoadMore,
    loadingMore,
    onVideoDelete,
    hasUnprocessedVideos,
  } = useVideoSlideshow({
    albumId,
  });
  const { data: { me: user } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });
  const [videos, setVideos] = useState([]);
  const [activeSlideshowVideo, setActiveSlideshowVideo] = useState(null);
  const [videosLayout, setVideosLayout] = useState(
    ALBUM_SLIDESHOW_VIDEO_LAYOUT.LANDSCAPE,
  );

  useEffect(() => {
    if (slideshow?.videos) {
      setVideos(slideshow.videos.records);

      if (
        activeSlideshowVideo &&
        !(slideshow?.videos?.records ?? []).find(
          ({ video }) => video.id === activeSlideshowVideo.video.id,
        )
      ) {
        setActiveSlideshowVideo(null);
      } else if (!activeSlideshowVideo && slideshow.videos.records.length > 0) {
        setActiveSlideshowVideo(slideshow.videos.records[0]);
      }
    }

    if (slideshow?.videos_layout) {
      setVideosLayout(slideshow.videos_layout);
    }
  }, [album, slideshow?.videos]);

  const activeVideoIdx = videos.findIndex(
    ({ video }) => activeSlideshowVideo?.video.id === video.id,
  );
  const nextActiveVideoId = (() => {
    if (activeVideoIdx === -1) {
      return null;
    }
    // Since this will always be the 2nd or greatest, don't care for asset_image
    if (activeVideoIdx >= 0 && activeVideoIdx < videos.length - 1) {
      return videos[activeVideoIdx + 1].video.id;
    }

    return videos[0].video.id;
  })();

  const {
    isPlaying,
    videoPlayerRef,
    onThumbnailClick,
    onPlaybackStart,
    onPlaybackStop,
  } = useVideoSlideshowPlayback({
    videos,
    video: activeSlideshowVideo?.video,
    nextVideoId: nextActiveVideoId ?? videos?.[0]?.video?.id,
    onChangeVideo: ({ nextVideoId }) => {
      if (nextVideoId !== null) {
        setActiveSlideshowVideo(
          videos.find(({ video }) => video.id === nextVideoId),
        );
      }
    },
    allowAutoPlay: false,
    allowControls: false,
  });

  if (!user || !album || !slideshow) {
    return (
      <div className="w-100vw h-100vh d-flex align-items-center justify-content-center">
        <Spinner />
      </div>
    );
  }

  // If not the owner, you should not be here.
  if (!isAlbumOwner(user, album)) {
    history.replace(
      `${basename ? `/app/${basename}` : ''}/albums/${albumId}/slideshow`,
    );
  }

  const firstVideo = slideshow?.videos?.records[0]?.video;

  const videoClasses = classNames({
    'video-slideshow-designer-video-iframe': true,
    'video-slideshow-designer-video-iframe-active': isPlaying,
    portrait: videosLayout === ALBUM_SLIDESHOW_VIDEO_LAYOUT.PORTRAIT,
  });
  const videoOverlayClasses = classNames({
    'video-slideshow-designer-video-overlay': true,
    'video-slideshow-designer-video-overlay-active': !isPlaying,
  });

  return (
    <DesignEditor className="slideshow-designer video-designer">
      <DesignEditorHeader>
        <DesignEditorHeaderLeftSlot>
          <Button
            btnStyle="link"
            className="text-extra-bold d-flex align-items-center"
            onClick={() => {
              if (isNativeView) {
                postNativeAppAlbumSlideshowDesignerDismiss();
              } else {
                history.goBack();
              }
            }}
          >
            <Angle
              direction="left"
              className="mr-1 video-slideshow-designer-back-angle"
            />
            <span className="d-none d-md-block">Back</span>
          </Button>
        </DesignEditorHeaderLeftSlot>
      </DesignEditorHeader>

      <DesignEditorControls
        className={classNames({
          'd-none': slideshow?.videos?.total === 0,
        })}
      >
        <DesignEditorSecondaryControls>
          <SecondaryVideosView
            albumId={albumId}
            albumSubtype={album?.album_subtype}
            slideshowVideos={videos}
            hasUnprocessedVideos={hasUnprocessedVideos}
            currentVideoId={activeSlideshowVideo?.video.id}
            videosTotal={slideshow?.videos?.total ?? 0}
            videosOffset={Math.max(
              (slideshow?.videos?.records?.length ?? 10) - 10,
              0,
            )}
            onReorderVideos={async reorderedVideos => {
              if (saving) {
                return;
              }

              try {
                setVideos(reorderedVideos);
                await onSlideshowUpdate({
                  createVideos: reorderedVideos.map(({ video }, index) => ({
                    video_id: video.id,
                    display_order: index,
                  })),
                });
              } catch (e) {
                openAlert({
                  type: 'error',
                  message: getErrorString(e),
                });
              }
            }}
            onMoveVideo={async ({ videoIdx, direction }) => {
              const reorderedVideos = arrayMove(
                videos,
                videoIdx,
                videoIdx + direction,
              );
              if (saving) {
                return;
              }

              try {
                setVideos(reorderedVideos);
                await onSlideshowUpdate({
                  createVideos: reorderedVideos.map(({ video }, index) => ({
                    video_id: video.id,
                    display_order: index,
                  })),
                });
              } catch (e) {
                openAlert({
                  type: 'error',
                  message: getErrorString(e),
                });
              }
            }}
            onDeleteVideo={async ({ videoIdx, video }) => {
              await onVideoDelete(video);
              const nextVideoIdx = (() => {
                if (videoIdx < videos.length - 1 && videos.length > 1) {
                  return videoIdx + 1;
                }
                if (videoIdx === videos.length - 1 && videos.length > 1) {
                  return videoIdx - 1;
                }
                return null;
              })();
              if (nextVideoIdx === null) {
                setActiveSlideshowVideo(null);
              } else {
                setActiveSlideshowVideo(videos[nextVideoIdx]);
              }
            }}
            loadingMore={loadingMore}
            onLoadMore={onLoadMore}
            onThumbnailClick={onThumbnailClick}
            isPlaying={isPlaying}
            visible={activeControlPane === CONTROL_PANE.VIDEOS}
          />
        </DesignEditorSecondaryControls>
      </DesignEditorControls>

      <DesignEditorPreviewPane>
        <DesignEditorPreviewContent>
          <Fragment>
            <iframe
              className={videoClasses}
              title={firstVideo?.name}
              src={firstVideo?.embed_url}
              allow="autoplay"
              ref={videoPlayerRef}
            />
            <Button
              btnStyle="reset"
              onClick={() => {
                if (isPlaying) {
                  onPlaybackStop();
                } else {
                  onPlaybackStart();
                }
              }}
              className={videoOverlayClasses}
            >
              <PlayButtonIcon size={80} color="#fff" />
            </Button>
          </Fragment>
          {videos.length === 0 && (
            <div className="slideshow-designer-videos-null-state">
              <img
                alt="Waldo logo"
                className="mb-2 grayscale-faded"
                width="48px"
                src={getAsset('/waldo-icon.svg')}
              />
              There are no videos yet. <br />
              {hasUnprocessedVideos && (
                <span className="text-primary text-xs">
                  Some videos are still being processed...
                </span>
              )}
              <div className="d-flex flex-column flex-md-row px-3 px-md-6 mt-2 w-100">
                <Button
                  btnBlock
                  btnOutline
                  className="mr-md-1"
                  onClick={() => {
                    history.push({
                      pathname: `/contributors/upload/${albumId}/record-video`,
                      search: `?${qs.stringify({
                        next: location.pathname,
                        generic_upload: isVGBContext(album) ? '0' : '1',
                      })}`,
                    });
                  }}
                >
                  {isVGBContext(album)
                    ? 'Add a custom greeting'
                    : 'Add a video'}
                </Button>
                <Button
                  btnBlock
                  btnOutline
                  className="mt-md-0 ml-md-1"
                  onClick={() =>
                    openModal(CONTRIBUTOR_SHARE_MODAL, { albumId })
                  }
                >
                  Invite
                </Button>
              </div>
            </div>
          )}
        </DesignEditorPreviewContent>
      </DesignEditorPreviewPane>
      <AlbumSlideshowShareModal
        album={album}
        albumId={albumId}
        designerOptions={{
          onPresent: () => {
            history.push(
              `${
                basename ? `/app/${basename}` : ''
              }/albums/${albumId}/slideshow?present=1`,
            );

            closeModal(ALBUM_SLIDESHOW_SHARE_MODAL);
          },
          // If we don't have a basename, going back to the event will not be available
          // this is to avoid directing the user to the wrong basename, which will, in
          // the end, result in the user going back to the home page.
          onShare: () => {
            openModal(CONTRIBUTOR_SHARE_MODAL);
            closeModal(ALBUM_SLIDESHOW_SHARE_MODAL);
          },
        }}
      />
      <ContributorShare />
      <ContactCircleSaveModal />
      <ContactCircleShareModal />
    </DesignEditor>
  );
};

VideoSlideshowDesignerView.propTypes = propTypes;

export default compose(withRouter)(VideoSlideshowDesignerView);
